import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardList, Plus, Search, Loader2 } from 'lucide-react';
import { API } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { fmtINR } from '../../../components/admin/billing/shared';
import { StatusPill, vehicleLabel } from '../../../components/admin/jobcards/shared';

const FILTERS = [
  ['active', 'In the bay'], ['open', 'Open'], ['in_progress', 'In progress'],
  ['ready', 'Ready'], ['invoiced', 'Invoiced'], ['cancelled', 'Cancelled'], ['', 'All'],
];

const Row = ({ c, onOpen }) => (
  <tr onClick={onOpen} className="cursor-pointer border-t border-slate-100 hover:bg-teal-50/40 transition-colors" data-testid={`jc-row-${c.id}`}>
    <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-900">{c.number}</td>
    <td className="px-4 py-3">
      <div className="font-medium text-slate-900">{c.customer_snapshot?.name}</div>
      <div className="text-xs text-slate-500">{vehicleLabel(c.vehicle_snapshot) || '—'}</div>
    </td>
    <td className="hidden px-4 py-3 text-xs text-slate-600 md:table-cell">{(c.technicians || []).map((t) => t.name).join(', ') || '—'}</td>
    <td className="px-4 py-3"><StatusPill status={c.status} testid={`jc-row-status-${c.id}`} /></td>
    <td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-900">₹ {fmtINR(c.total)}</td>
    <td className="hidden px-4 py-3 text-xs text-slate-500 sm:table-cell">{(c.created_at || '').slice(0, 10)}</td>
  </tr>
);

export default function JobCardsList() {
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('active');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    let live = true;
    const t = setTimeout(() => {
      axios.get(`${API}/job-cards`, { params: { status: status || undefined, q: q.trim() || undefined }, headers: authHeader() })
        .then((r) => { if (live) setRows(r.data || []); }).catch(() => { if (live) setRows([]); })
        .finally(() => { if (live) setLoading(false); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [status, q, authHeader]);

  return (
    <div className="min-h-full space-y-4 bg-slate-50 p-4 sm:p-6" data-testid="job-cards-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="text-teal-600" size={22} />
          <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">Job Cards</h1>
        </div>
        <Link to="/admin/job-cards/new" className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700" data-testid="jc-new-btn">
          <Plus size={15} /> New job card
        </Link>
      </div>
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="flex flex-wrap gap-1.5" data-testid="jc-filters">
          {FILTERS.map(([key, label]) => (
            <button key={key || 'all'} type="button" onClick={() => setStatus(key)} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${status === key ? 'btn-ink' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'}`} data-testid={`jc-filter-${key || 'all'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative md:ml-auto md:w-80">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Card no., customer, phone or reg. no." className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-teal-400" data-testid="jc-search" />
        </div>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-2">Card</th><th className="px-4 py-2">Customer &amp; vehicle</th><th className="hidden px-4 py-2 md:table-cell">Technicians</th><th className="px-4 py-2">Stage</th><th className="px-4 py-2 text-right">Total</th><th className="hidden px-4 py-2 sm:table-cell">Opened</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500"><Loader2 size={16} className="inline animate-spin" /></td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500" data-testid="jc-empty">No job cards here yet.</td></tr>}
            {!loading && rows.map((c) => <Row key={c.id} c={c} onOpen={() => navigate(`/admin/job-cards/${c.id}`)} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
